/**
 * What the notifications stream delivers: the typed frames below, and the cloud's status frames,
 * which have no type. src/api/notifications.ts parses both into one union of events.
 */

import { z } from 'zod';

import { checkDeviceIdOf, DeviceIdSchema } from './deviceId';
import { HealthFrameSchema } from './healthMessages';

// The media_server's status enum grows with it; '' is a status the UI does not know.
export const ResourceStatusSchema = z.enum(['offline', 'unauthorized', 'online', 'recording', ''])
    .catch('');
/** Live status of a device or a media_server. */
export type ResourceStatus = z.infer<typeof ResourceStatusSchema>;

/** A media_server's word on a device; the cloud's own arrives as a status DTO. */
export const DeviceStatusChangedSchema = z.object({
    type: z.literal('deviceStatusChanged'),
    deviceId: DeviceIdSchema,
    status: ResourceStatusSchema,
});
export type DeviceStatusChanged = z.infer<typeof DeviceStatusChangedSchema>;

/** Made from the cloud's `ms.<serverId>` status frame; no backend sends it. */
export interface ServerStatusChanged {
    type: 'serverStatusChanged';
    serverId: string;
    status: ResourceStatus;
}

/**
 * A row of the cloud's changelog without its data; the cloud names tables the UI ignores. The
 * id is a device id for the `devices` resource only.
 */
export const ChangeFrameSchema = z.object({
    type: z.literal('change'),
    resource: z.string(),
    action: z.enum(['upsert', 'delete']),
    id: z.string(),
    serverId: z.string(),
}).check(checkDeviceIdOf('id', (frame) => frame.resource === 'devices'));
export type ChangeFrame = z.infer<typeof ChangeFrameSchema>;

// A media_server adds severities with its own releases.
export const RuleSeveritySchema = z.enum(['info', 'warning', 'alert']).catch('info');

/**
 * A rule's `notify` action. Only what the UI shows is declared: the media_server sends more,
 * and the cloud forwards an older or newer server's frame unchecked.
 */
export const RuleNotificationSchema = z.object({
    type: z.literal('ruleNotification'),
    id: z.string(),
    serverId: z.string(),
    ruleName: z.string(),
    severity: RuleSeveritySchema,
    title: z.string(),
    body: z.string(),
    /** The camera of the event that fired the rule; the null id when no camera fired it. */
    sourceDeviceId: DeviceIdSchema,
    /** When that event started, UTC ms; 0 when the firing has no event time. */
    timestampMs: z.number(),
    /** When that event ended; absent for an instant or open-ended firing. */
    endTimeMs: z.number().optional(),
    /** Attached only when the rule asked for a snapshot. */
    image: z.object({
        mimeType: z.string(),
        /** Base64, so it drops into a data URI. */
        data: z.string(),
    }).optional(),
});
export type RuleNotification = z.infer<typeof RuleNotificationSchema>;

/** Every typed frame the cloud and the media_server put on the stream. */
export const NotificationFrameSchema = z.discriminatedUnion('type', [
    DeviceStatusChangedSchema,
    ChangeFrameSchema,
    RuleNotificationSchema,
    HealthFrameSchema,
]);
export type NotificationFrame = z.infer<typeof NotificationFrameSchema>;

/** nx::vms::api::ResourceStatus by number, as the cloud's status frames carry it. */
export const ResourceStatusCode = {
    Offline: 0,
    Unauthorized: 1,
    Online: 2,
    Recording: 3,
    Unknown: -1,
} as const;
export type ResourceStatusCode = typeof ResourceStatusCode[keyof typeof ResourceStatusCode];

// The media_server writes the number of its own enum, which grows with it.
export const ResourceStatusCodeSchema = z.enum(ResourceStatusCode)
    .catch(ResourceStatusCode.Unknown);

/** How a device's key in the cloud's `status-<orgId>` bucket starts; the rest is its id. */
export const DEVICE_STATUS_KEY_PREFIX = 'cam.';

/** How a media_server's key in the same bucket starts; the rest is its id. */
export const SERVER_STATUS_KEY_PREFIX = 'ms.';

/** A device's entry of the cloud's `status-<orgId>` bucket, written by its media_server. */
export const DeviceStatusDtoSchema = z.object({
    key: z.templateLiteral([DEVICE_STATUS_KEY_PREFIX, DeviceIdSchema]),
    value: ResourceStatusCodeSchema,
});
export type DeviceStatusDto = z.infer<typeof DeviceStatusDtoSchema>;

/** A media_server's entry of the same bucket, written by the cloud from its relay tunnel. */
export const ServerStatusDtoSchema = z.object({
    key: z.templateLiteral([SERVER_STATUS_KEY_PREFIX, z.string()]),
    value: ResourceStatusCodeSchema,
});
export type ServerStatusDto = z.infer<typeof ServerStatusDtoSchema>;

/** How an I/O module's key in the same bucket starts; the rest is its device id. */
export const IO_STATE_KEY_PREFIX = 'io.';

/** Where an output is in its cycle; an input is always ready. */
export const OutputPhase = {
    Ready: 'ready',
    Active: 'active',
    CoolingDown: 'coolingDown',
    /** A phase of a newer media_server; the UI shows the output as not ready to fire. */
    Unknown: 'unknown',
} as const;
export type OutputPhase = typeof OutputPhase[keyof typeof OutputPhase];

// The media_server's output state machine may gain a phase.
export const OutputPhaseSchema = z.enum(OutputPhase).catch(OutputPhase.Unknown);

/** The live value of one port of an I/O module. */
export const PortStateSchema = z.object({
    /** 1-based. */
    port: z.int(),
    /** At the level opposite the port's normal state. */
    active: z.boolean(),
    phase: OutputPhaseSchema,
});
export type PortState = z.infer<typeof PortStateSchema>;

/** An I/O module's entry of the same bucket: its I/O state, as its media_server writes it. */
export const IoStateDtoSchema = z.object({
    key: z.templateLiteral([IO_STATE_KEY_PREFIX, DeviceIdSchema]),
    value: z.object({ ports: z.array(PortStateSchema) }),
});
export type IoStateDto = z.infer<typeof IoStateDtoSchema>;

/** Made from the cloud's `io.<deviceId>` frame; no backend sends it. */
export interface IoStateChanged {
    type: 'ioStateChanged';
    deviceId: string;
    ports: PortState[];
}

export type NotificationEvent = NotificationFrame | ServerStatusChanged | IoStateChanged;

export type NotificationListener = (event: NotificationEvent) => void;

/** What the stream itself reports, apart from the frames it carries. */
export enum StreamEvent {
    /** A connection opened. Each one starts with every current status. */
    Open = 'open',
    /** No connection has been open for the whole grace period. */
    Down = 'down',
}

export type StreamEventListener = (event: StreamEvent) => void;

export enum StreamInputKind {
    Frame = 'frame',
    /** The stream opened again after a loss, so `change` frames may have gone missing. */
    Reopened = 'reopened',
    /** The stream is gone past its grace period; nothing pushed can be vouched for. */
    Down = 'down',
}

/** What a stream handler is given: a frame, or news about the stream carrying them. */
export type StreamInput =
    { kind: StreamInputKind.Frame; frame: NotificationEvent } |
    { kind: StreamInputKind.Reopened } |
    { kind: StreamInputKind.Down };
